// This file configures the initialization of Sentry on the client.
// The added config here will be used whenever a users loads a page in their browser.
// https://docs.sentry.io/platforms/javascript/guides/nextjs/

;globalThis["_sentryRouteManifest"] = "{\"dynamicRoutes\":[{\"path\":\"/:rest*\",\"regex\":\"^/(.+)$\",\"paramNames\":[\"rest\"],\"hasOptionalPrefix\":false},{\"path\":\"/pricesheets/:priceSheetID\",\"regex\":\"^/pricesheets/([^/]+)$\",\"paramNames\":[\"priceSheetID\"],\"hasOptionalPrefix\":false},{\"path\":\"/pricesheets/:priceSheetID/products/:productID\",\"regex\":\"^/pricesheets/([^/]+)/products/([^/]+)$\",\"paramNames\":[\"priceSheetID\",\"productID\"],\"hasOptionalPrefix\":false},{\"path\":\"/partners/:organizationPartnerModelKey\",\"regex\":\"^/partners/([^/]+)$\",\"paramNames\":[\"organizationPartnerModelKey\"],\"hasOptionalPrefix\":false},{\"path\":\"/claim-management/claims/:claimId/claim-details\",\"regex\":\"^/claim-management/claims/([^/]+)/claim-details$\",\"paramNames\":[\"claimId\"],\"hasOptionalPrefix\":false},{\"path\":\"/claim-management/claims/:claimId/claim-details/:transactionID\",\"regex\":\"^/claim-management/claims/([^/]+)/claim-details/([^/]+)$\",\"paramNames\":[\"claimId\",\"transactionID\"],\"hasOptionalPrefix\":false},{\"path\":\"/claim-management/:tabName\",\"regex\":\"^/claim-management/([^/]+)$\",\"paramNames\":[\"tabName\"],\"hasOptionalPrefix\":false},{\"path\":\"/branches/:tab\",\"regex\":\"^/branches/([^/]+)$\",\"paramNames\":[\"tab\"],\"hasOptionalPrefix\":false},{\"path\":\"/tasks/:taskID\",\"regex\":\"^/tasks/([^/]+)$\",\"paramNames\":[\"taskID\"],\"hasOptionalPrefix\":false},{\"path\":\"/agreements/:agreementModelKey\",\"regex\":\"^/agreements/([^/]+)$\",\"paramNames\":[\"agreementModelKey\"],\"hasOptionalPrefix\":false},{\"path\":\"/agreements/:agreementModelKey/:tab\",\"regex\":\"^/agreements/([^/]+)/([^/]+)$\",\"paramNames\":[\"agreementModelKey\",\"tab\"],\"hasOptionalPrefix\":false},{\"path\":\"/agreements/:agreementModelKey/:tab/:productId\",\"regex\":\"^/agreements/([^/]+)/([^/]+)/([^/]+)$\",\"paramNames\":[\"agreementModelKey\",\"tab\",\"productId\"],\"hasOptionalPrefix\":false},{\"path\":\"/admin/organizations/:organizationID\",\"regex\":\"^/admin/organizations/([^/]+)$\",\"paramNames\":[\"organizationID\"],\"hasOptionalPrefix\":false},{\"path\":\"/admin/organizations/:organizationID/:tab\",\"regex\":\"^/admin/organizations/([^/]+)/([^/]+)$\",\"paramNames\":[\"organizationID\",\"tab\"],\"hasOptionalPrefix\":false}],\"staticRoutes\":[{\"path\":\"/signin\"},{\"path\":\"/\"},{\"path\":\"/pricesheets\"},{\"path\":\"/profile\"},{\"path\":\"/analytics/unpriced-agreement-items\"},{\"path\":\"/analytics/spa-pair-comparison\"},{\"path\":\"/analytics/spa-revision-comparison\"},{\"path\":\"/analytics/impact-report\"},{\"path\":\"/analytics/claim-rejection\"},{\"path\":\"/analytics/advanced\"},{\"path\":\"/analytics/price-sheet-comparison\"},{\"path\":\"/users\"},{\"path\":\"/price-sync\"},{\"path\":\"/price-sync/history\"},{\"path\":\"/price-sync/cross-reference\"},{\"path\":\"/price-sync/export-setup\"},{\"path\":\"/price-sync/export-format\"},{\"path\":\"/price-sync/formats\"},{\"path\":\"/partners\"},{\"path\":\"/claim-management\"},{\"path\":\"/products\"},{\"path\":\"/branches\"},{\"path\":\"/tasks\"},{\"path\":\"/agreements\"},{\"path\":\"/admin\"},{\"path\":\"/admin/organizations\"},{\"path\":\"/admin/ingestion\"}],\"isrRoutes\":[]}";globalThis["_sentryNextJsVersion"] = "16.4.3";globalThis["_sentryRewritesTunnelPath"] = "/monitoring";import { SENTRY_DSN } from '@/config/sentry'
import { isHandledSignOut } from '@/helpers/handled-signout-telemetry'
import * as Sentry from '@sentry/nextjs'

const environment = process.env.NEXT_PUBLIC_SENTRY_ENVIRONMENT || 'local'
const isProduction = environment === 'prod'
const isDeployed = environment === 'prod' || environment === 'qa'

if (isDeployed) {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: environment,
    release: process.env.NEXT_PUBLIC_SENTRY_RELEASE,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.consoleLoggingIntegration({ levels: ['log', 'warn', 'error'] }),
      Sentry.replayIntegration({
        maskAllInputs: false,
        maskAllText: false,
        blockAllMedia: false
      })
    ],
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: isProduction ? 1.0 : 0.0,
    replaysOnErrorSampleRate: 1.0,
    beforeSend(event, hint) {
      // Only the exact first-party Axios 401 whose sign-out completed is an
      // expected rejection. Other 401s (including failed sign-outs) still report.
      if (isHandledSignOut(hint.originalException)) {
        return null
      }
      // A failed resource load (e.g. <head> <link>) fires a DOM Event, not an
      // Error. The global handler captures it as a promise rejection with no
      // stack. Mostly bots and blocked third-party assets — but one of our own
      // chunks failing to load breaks the page for a real user and arrives the
      // same way, so a same-origin resource still reports.
      if (
        typeof Event !== 'undefined' &&
        hint.originalException instanceof Event
      ) {
        const target = hint.originalException.target as
          (Element & { src?: string; href?: string }) | null
        const resource = target?.src ?? target?.href
        if (!resource || !resource.startsWith(window.location.origin)) {
          return null
        }
      }
      // A failed fetch inside the PostHog session-replay recorder (third-party
      // script), not our code. Transient and unactionable.
      const frames = event.exception?.values?.flatMap(
        (value) => value.stacktrace?.frames ?? []
      )
      if (
        frames?.some((frame) => frame.filename?.includes('posthog-recorder'))
      ) {
        return null
      }
      // "Object Not Found Matching Id:..." is injected by a browser extension /
      // antivirus, not first-party code. Drop the whole non-Error rejection class.
      const message =
        typeof hint.originalException === 'string'
          ? hint.originalException
          : (event.exception?.values?.[0]?.value ?? '')
      if (message.includes('Object Not Found Matching Id')) {
        return null
      }
      // Browser-extension noise: a WebExtension content script's
      // runtime.sendMessage() fails when its background (browser) tab is gone
      // ("... Tab not found."). Thrown inside the extension, surfaced through our
      // global unhandledrejection handler. Not first-party code (WEB-3M). Matched
      // on the runtime.sendMessage marker only — never bare "Tab not found", so a
      // real app error mentioning a tab is not masked.
      if (message.includes('runtime.sendMessage')) {
        return null
      }
      const axiosError = hint.originalException as
        | {
            name?: string
            message?: string
            code?: string
            response?: { status?: number }
          }
        | undefined
      // Transient no-response network failures (offline, cancelled, CORS). Not
      // bugs — the request never reached the server (no response attached).
      if (
        axiosError?.name === 'AxiosError' &&
        !axiosError.response &&
        (axiosError.code === 'ERR_NETWORK' ||
          axiosError.message === 'Network Error')
      ) {
        return null
      }
      // No 403 filter. A denial the UI already gates is indistinguishable from
      // an over-tight permission rule, so dropping the class hides the
      // regression. Prod saw one 403 in the 90 days before this was filtered —
      // the volume never justified the blind spot.
      return event
    }
  })
}

export const onRouterTransitionStart = Sentry.captureRouterTransitionStart
